import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Save, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function EditFlightModal({ isOpen, onClose, flights, onSave }) {
  const [editableFlights, setEditableFlights] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);

  useEffect(() => {
    if (flights) {
      setEditableFlights(JSON.parse(JSON.stringify(flights))); // Deep copy
      const flightWithPrice = flights.find((f) => f.total_price && f.total_price > 0);
      setTotalPrice(flightWithPrice ? flightWithPrice.total_price : 0);
    }
  }, [flights]);

  if (!isOpen) return null;

  const handleFlightChange = (index, field, value) => {
    const newFlights = [...editableFlights];
    newFlights[index][field] = value;
    setEditableFlights(newFlights);
  };

  const addFlight = () => {
    setEditableFlights([...editableFlights, {
      marketing_airline: '',
      flight_number: '',
      departure_city: '',
      arrival_city: '',
      departure_date: '',
      departure_time: '',
      arrival_date: '',
      arrival_time: ''
    }]);
  };

  const removeFlight = (index) => {
    if (editableFlights.length > 1) {
      const newFlights = editableFlights.filter((_, i) => i !== index);
      setEditableFlights(newFlights);
    }
  };

  const handleSave = () => {
    // Distribute the total price to one of the flights for saving
    const flightsToSave = editableFlights.map((flight, index) => ({
      ...flight,
      total_price: index === 0 ? totalPrice : 0
    }));
    onSave(flightsToSave);
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-start justify-center pt-20 pb-24 md:pb-4 md:pt-4 md:items-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}>

      <motion.div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }} />


      <motion.div
        className="relative glass rounded-2xl p-4 sm:p-6 max-w-3xl w-full max-h-full overflow-y-auto"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}>

        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Edit Itinerary Details</h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="hover:bg-white/10 rounded-full">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label className="text-white">Total Price</Label>
            <Input
              type="number"
              value={totalPrice}
              onChange={(e) => setTotalPrice(parseFloat(e.target.value) || 0)}
              className="glass text-white border-white/20 focus:border-white/40"
              placeholder="e.g., 1234.56" />

          </div>

          {editableFlights.map((flight, index) =>
          <motion.div
            key={index}
            className="glass-light rounded-xl p-4 space-y-4 relative"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}>

              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-white">Flight {index + 1}</h3>
                {editableFlights.length > 1 &&
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeFlight(index)}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 rounded-full">

                    <Trash2 className="w-4 h-4" />
                  </Button>
              }
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-gray-300">Airline</Label>
                  <Input value={flight.marketing_airline || ''} onChange={(e) => handleFlightChange(index, 'marketing_airline', e.target.value)} className="glass text-white border-white/20" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-300">Flight Number</Label>
                  <Input value={flight.flight_number || ''} onChange={(e) => handleFlightChange(index, 'flight_number', e.target.value)} className="glass text-white border-white/20" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-300">Departure City</Label>
                  <Input value={flight.departure_city || ''} onChange={(e) => handleFlightChange(index, 'departure_city', e.target.value)} className="glass text-white border-white/20" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-300">Arrival City</Label>
                  <Input value={flight.arrival_city || ''} onChange={(e) => handleFlightChange(index, 'arrival_city', e.target.value)} className="glass text-white border-white/20" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-300">Departure Date</Label>
                  <Input type="text" value={flight.departure_date || ''} onChange={(e) => handleFlightChange(index, 'departure_date', e.target.value)} className="glass text-white border-white/20" placeholder="MM/DD/YYYY" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-300">Departure Time</Label>
                  <Input type="time" value={flight.departure_time || ''} onChange={(e) => handleFlightChange(index, 'departure_time', e.target.value)} className="glass text-white border-white/20" />
                </div>
                 <div className="space-y-2">
                  <Label className="text-sm text-gray-300">Arrival Date</Label>
                  <Input type="text" value={flight.arrival_date || ''} onChange={(e) => handleFlightChange(index, 'arrival_date', e.target.value)} className="glass text-white border-white/20" placeholder="MM/DD/YYYY" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-300">Arrival Time</Label>
                  <Input type="time" value={flight.arrival_time || ''} onChange={(e) => handleFlightChange(index, 'arrival_time', e.target.value)} className="glass text-white border-white/20" />
                </div>
              </div>
            </motion.div>
          )}
          
          <Button onClick={addFlight} variant="outline" className="bg-primary text-white px-4 py-2 text-sm font-medium inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border hover:bg-accent hover:text-accent-foreground h-10 w-full border-white/20 hover:glass">
            <Plus className="w-4 h-4 mr-2" />
            Add Connecting Flight
          </Button>
        </div>

        <div className="mt-8 flex justify-end">
          <Button onClick={handleSave} className="bg-[var(--accent-color)] hover:bg-primary/90 text-white hover-glow">
            <Save className="w-4 h-4 mr-2" />
            Save Changes
          </Button>
        </div>
      </motion.div>
    </motion.div>);

}